import React, { useState } from 'react';
import {
  Typography, Paper, TextField, Grid, Button, MenuItem,
  FormHelperText, Box, Container,
  Card, CardContent,
} from '@mui/material';
import { Save, CheckCircle, Close } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from '../utils/api';
import { US_STATES } from '../utils/constants';

const phoneTypes = [
  { value: 'office', label: 'Work' },
  { value: 'cell', label: 'Cellphone' },
  { value: 'other', label: 'Other' },
];

interface ReferralOnlyRegistrationFormProps {
  onBackToChoice: () => void;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  firmName?: string;
  phoneNumbers?: string;
  primaryOffice?: {
    city?: string;
    state?: string;
  };
  stateLicenses?: string;
}

const ReferralOnlyRegistrationFormInner: React.FC<ReferralOnlyRegistrationFormProps> = ({ onBackToChoice }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [registrationEmail, setRegistrationEmail] = useState('');

  const [form, setForm] = useState({
    firstName: '',
    middleInitial: '',
    lastName: '',
    email: '',
    firmName: '',
  });

  const [phoneNumbers, setPhoneNumbers] = useState([
    { number: '', type: 'office' },
    { number: '', type: 'cell' },
  ]);

  const [primaryOffice, setPrimaryOffice] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zipCode: '',
  });

  const [stateLicenses, setStateLicenses] = useState([
    { state: '', barIdNo: '' },
  ]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(cleaned);
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phoneNumber;
  };

  const handlePhoneBlur = (idx: number) => {
    const phone = phoneNumbers[idx];
    if (phone.number.trim()) {
      const formatted = formatPhoneNumber(phone.number);
      setPhoneNumbers(prev => prev.map((p, i) => i === idx ? { ...p, number: formatted } : p));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name as keyof ValidationErrors]) {
      setValidationErrors({ ...validationErrors, [e.target.name]: undefined });
    }
  };

  const handlePhoneChange = (idx: number, field: string, value: string) => {
    setPhoneNumbers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    if (validationErrors.phoneNumbers) {
      setValidationErrors({ ...validationErrors, phoneNumbers: undefined });
    }
  };

  const handlePrimaryOfficeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrimaryOffice({ ...primaryOffice, [e.target.name]: e.target.value });
    if (validationErrors.primaryOffice?.[e.target.name as keyof typeof validationErrors.primaryOffice]) {
      setValidationErrors({
        ...validationErrors,
        primaryOffice: {
          ...validationErrors.primaryOffice,
          [e.target.name]: undefined,
        },
      });
    }
  };

  const handleStateLicenseChange = (idx: number, field: string, value: string) => {
    setStateLicenses(prev => prev.map((lic, i) => i === idx ? { ...lic, [field]: value } : lic));
    if (validationErrors.stateLicenses) {
      setValidationErrors({ ...validationErrors, stateLicenses: undefined });
    }
  };

  const addStateLicense = () => {
    setStateLicenses(prev => [...prev, { state: '', barIdNo: '' }]);
  };

  const removeStateLicense = (idx: number) => {
    setStateLicenses(prev => prev.filter((_, i) => i !== idx));
  };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};

    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(form.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!form.firmName.trim()) errors.firmName = 'Firm name is required';

    if (!phoneNumbers[0].number.trim()) {
      errors.phoneNumbers = 'Primary phone number is required';
    } else if (!validatePhoneNumber(phoneNumbers[0].number)) {
      errors.phoneNumbers = 'Please enter a valid primary phone number';
    } else if (phoneNumbers[1]?.number.trim() && !validatePhoneNumber(phoneNumbers[1].number)) {
      errors.phoneNumbers = 'Please enter a valid secondary phone number';
    }

    if (!primaryOffice.city.trim()) {
      errors.primaryOffice = { ...errors.primaryOffice, city: 'City is required' };
    }
    if (!primaryOffice.state.trim()) {
      errors.primaryOffice = { ...errors.primaryOffice, state: 'State is required' };
    }

    const validLicenses = stateLicenses.filter(l => l.state && l.barIdNo);
    if (validLicenses.length === 0) {
      errors.stateLicenses = 'At least one state license is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    if (!executeRecaptcha) {
      setSubmitError('reCAPTCHA not loaded. Please refresh the page and try again.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const recaptchaToken = await executeRecaptcha('submit');

      const payload = {
        registrationType: 'referral',
        recaptchaToken,
        firstName: form.firstName,
        middleInitial: form.middleInitial,
        lastName: form.lastName,
        email: form.email,
        firmName: form.firmName,
        phoneNumbers: phoneNumbers.filter(p => p.number.trim()),
        primaryOffice: {
          line1: primaryOffice.line1,
          line2: primaryOffice.line2 || undefined,
          city: primaryOffice.city,
          state: primaryOffice.state,
          zipCode: primaryOffice.zipCode,
        },
        stateLicenses: stateLicenses.filter(l => l.state && l.barIdNo),
        proBonoInterest: false,
        disciplinary: [{ hasAction: false, narrative: '' }],
      };

      await axios.post('/api/lawyers/register', payload);
      setRegistrationEmail(form.email);
      setSubmitted(true);
      setSubmitError(null);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || 'Failed to submit registration.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="md">
        <Card sx={{ mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Registration Submitted!
            </Typography>
            <Box sx={{ p: 3, textAlign: 'left', mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Verify Your Email Address
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We've sent a verification email to <strong>{registrationEmail}</strong>. Please check your Inbox and click the verification link to complete your registration.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Once your email is verified, you can request referrals for your clients by clicking the link in that email.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The verification link will expire in 4 hours.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Register to Find Lawyers for your Clients
          </Typography>
          <Button
            component={Link}
            to="/register/edit-request"
            variant="text"
            color="primary"
            sx={{ textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' }, whiteSpace: 'normal', textAlign: 'left' }}
          >
            Already Registered? You can edit your registration here
          </Button>
        </Box>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Complete the form below to register and find lawyers for your clients. All fields marked with * are required.
        </Typography>

        <form onSubmit={e => e.preventDefault()}>
          <Grid container spacing={2}>

            {/* Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Middle Initial"
                name="middleInitial"
                value={form.middleInitial}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
              />
            </Grid>

            {/* Email & Firm */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
                fullWidth
                required
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Firm Name"
                name="firmName"
                value={form.firmName}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.firmName}
                helperText={validationErrors.firmName}
              />
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Phone Numbers *
              </Typography>
            </Grid>
            {phoneNumbers.map((phone, idx) => (
              <Grid item xs={12} key={idx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      select
                      label="Type"
                      value={phone.type}
                      onChange={e => handlePhoneChange(idx, 'type', e.target.value)}
                      fullWidth
                    >
                      {phoneTypes.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={idx === 0 ? 'Primary Number' : 'Secondary Number'}
                      value={phone.number}
                      onChange={e => handlePhoneChange(idx, 'number', e.target.value)}
                      onBlur={() => handlePhoneBlur(idx)}
                      fullWidth
                      type="tel"
                      required={idx === 0}
                      inputProps={{ maxLength: 20 }}
                      error={!!validationErrors.phoneNumbers}
                      helperText={
                        validationErrors.phoneNumbers && idx === 0
                          ? validationErrors.phoneNumbers
                          : idx === 1 ? 'Optional - Format: (xxx) xxx-xxxx' : 'Format: (xxx) xxx-xxxx'
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}

            {/* Primary Location */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Primary Location
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address Line 1"
                name="line1"
                value={primaryOffice.line1}
                onChange={handlePrimaryOfficeChange}
                fullWidth
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address Line 2"
                name="line2"
                value={primaryOffice.line2}
                onChange={handlePrimaryOfficeChange}
                fullWidth
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="City"
                name="city"
                value={primaryOffice.city}
                onChange={handlePrimaryOfficeChange}
                fullWidth
                required
                error={!!validationErrors.primaryOffice?.city}
                helperText={validationErrors.primaryOffice?.city}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="State"
                name="state"
                value={primaryOffice.state}
                onChange={handlePrimaryOfficeChange}
                fullWidth
                required
                error={!!validationErrors.primaryOffice?.state}
                helperText={validationErrors.primaryOffice?.state}
              >
                {US_STATES.map(state => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Zip Code"
                name="zipCode"
                value={primaryOffice.zipCode}
                onChange={handlePrimaryOfficeChange}
                fullWidth
              />
            </Grid>

            {/* State Bar Licenses */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                State Bar License(s) *
              </Typography>
            </Grid>
            {stateLicenses.map((lic, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="State"
                    value={lic.state}
                    onChange={e => handleStateLicenseChange(idx, 'state', e.target.value)}
                    fullWidth
                    required
                  >
                    {US_STATES.map(state => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Bar ID No."
                    value={lic.barIdNo}
                    onChange={e => handleStateLicenseChange(idx, 'barIdNo', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  {stateLicenses.length > 1 && (
                    <Button color="error" onClick={() => removeStateLicense(idx)} sx={{ mt: 1 }}>
                      Remove
                    </Button>
                  )}
                </Grid>
              </React.Fragment>
            ))}
            {validationErrors.stateLicenses && (
              <Grid item xs={12}>
                <FormHelperText error>{validationErrors.stateLicenses}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button onClick={addStateLicense} sx={{ mt: 1 }}>
                Add Another State License
              </Button>
            </Grid>

          </Grid>

          {submitError && (
            <Box sx={{ mt: 2 }}>
              <FormHelperText error sx={{ fontSize: '1rem' }}>{submitError}</FormHelperText>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'stretch',
            gap: 2,
            mt: 4,
          }}>
            <Button
              onClick={onBackToChoice}
              startIcon={<Close />}
              size="large"
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={<Save />}
              size="large"
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}
            >
              {submitting ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

const ReferralOnlyRegistrationForm: React.FC<ReferralOnlyRegistrationFormProps> = ({ onBackToChoice }) => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <ReferralOnlyRegistrationFormInner onBackToChoice={onBackToChoice} />
    </GoogleReCaptchaProvider>
  );
};

export default ReferralOnlyRegistrationForm;
